import type { ChangeLogEntry, HistoryEntityType } from '../types';

export type TimelineEventLike = {
  id: string;
  date: string;
  type: string;
  title: string;
  description: string;
};

/** Extrae "Por: Nombre" al final de descripciones del timeline legacy (p. ej. change_history). */
export function parseModifiedByFromDescription(description: string): { detail: string; modifiedBy: string } {
  const raw = String(description ?? '').trim();
  if (!raw) return { detail: '—', modifiedBy: 'Sistema' };
  const byMatch = raw.match(/\s*·?\s*Por:\s*(.+)$/i);
  if (byMatch?.[1]) {
    return {
      detail: raw.replace(/\s*·?\s*Por:\s*(.+)$/i, '').trim() || '—',
      modifiedBy: byMatch[1].trim(),
    };
  }
  return { detail: raw, modifiedBy: 'Sistema' };
}

function badgeForTimelineEventType(eventType: string): string {
  const t = eventType.toLowerCase();
  if (t === 'payment') return 'Pago';
  if (
    t.includes('investment') ||
    t.includes('investor_created') ||
    t.includes('property_created') ||
    t.includes('tenant_created') ||
    t.includes('tenant_added') ||
    t.includes('tenant_removed')
  ) {
    return 'Inversión';
  }
  if (t.includes('document')) return 'Documento';
  if (
    t.includes('maintenance') ||
    t.includes('delivery') ||
    t === 'inspection' ||
    t.includes('contract_cancelled') ||
    t.includes('contract_expired')
  ) {
    return 'Mantenimiento';
  }
  return 'Información';
}

/** Convierte respuestas de GET .../timeline al formato usado por HistoryTable. */
export function mapTimelineToChangeLogEntries(
  entityId: string,
  entityType: HistoryEntityType,
  events: TimelineEventLike[] | undefined,
): ChangeLogEntry[] {
  if (!Array.isArray(events) || events.length === 0) return [];
  return events.map((ev) => {
    const { detail, modifiedBy } = parseModifiedByFromDescription(ev.description);
    return {
      id: `timeline-${entityType}-${ev.id}`,
      entityId,
      entityType,
      type: badgeForTimelineEventType(ev.type),
      description: ev.title,
      oldValue: null,
      newValue: detail,
      modifiedBy,
      createdAt: ev.date,
    };
  });
}

/**
 * Combina filas derivadas del timeline (eventos reales en BD vía backend) con `change_logs` opcionales.
 * Orden: más reciente primero.
 */
export function mergeHistoryRows(
  fromTimeline: ChangeLogEntry[],
  fromChangeLogs: ChangeLogEntry[] | undefined,
): ChangeLogEntry[] {
  const persisted = Array.isArray(fromChangeLogs) ? fromChangeLogs : [];
  const map = new Map<string, ChangeLogEntry>();

  for (const row of persisted) {
    map.set(`id:${row.id}`, row);
  }
  for (const row of fromTimeline) {
    if (!map.has(`id:${row.id}`)) {
      map.set(`id:${row.id}`, row);
    }
  }

  const out = [...map.values()];
  out.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return out;
}
